import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { DOCUMENTS_PAGE_SIZE, DocumentListItemSchema, MAX_DOCUMENT_PAGES } from './documents';

/**
 * Where each document is in the pipeline, with real progress (T-0159).
 *
 * The engagement documents list (`GET /engagements/:id/documents`) carries,
 * next to the older `process_status`, the pipeline view built by
 * `apps/api/src/services/document-pipeline.ts`:
 *
 *   stage           uploading | reading | indexing | ready | failed
 *   progress        work done over work known for the current stage —
 *                   pages read, passages stored — or null when the amount
 *                   is not known (never an estimate)
 *   failed_stage    which stage failed; failure_reason says why in plain
 *                   words; failure_detail is the recorded technical reason
 *   retry_action    reprocess (read again) | reindex (index again) | null
 *                   (nothing to retry: upload the file again)
 *   page_count      pages read, once reading has finished
 *   attempt / retry_reason   a temporary failure being retried
 *
 * Every field is optional so an older API still parses (the screens then
 * fall back to `process_status`).
 */

export const DocumentStageSchema = z.enum(['uploading', 'reading', 'indexing', 'ready', 'failed']);
export type DocumentStage = z.infer<typeof DocumentStageSchema>;

export const StageProgressSchema = z.object({
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  unit: z.enum(['bytes', 'pages', 'passages']),
});
export type StageProgress = z.infer<typeof StageProgressSchema>;

export const DocumentProcessingItemSchema = DocumentListItemSchema.extend({
  stage: DocumentStageSchema.optional(),
  progress: StageProgressSchema.nullable().optional(),
  failed_stage: z.enum(['uploading', 'reading', 'indexing']).nullable().optional(),
  failure_reason: z.string().nullable().optional(),
  failure_detail: z.string().nullable().optional(),
  retry_action: z.enum(['reprocess', 'reindex']).nullable().optional(),
  page_count: z.number().int().nullable().optional(),
  attempt: z.number().int().nullable().optional(),
  retry_reason: z.string().nullable().optional(),
});
export type DocumentProcessingItem = z.infer<typeof DocumentProcessingItemSchema>;

export const DocumentProcessingListSchema = z.object({
  documents: z.array(DocumentProcessingItemSchema),
  next_cursor: z.string().optional(),
});
export type DocumentProcessingList = z.infer<typeof DocumentProcessingListSchema>;

/**
 * EVERY document in the engagement, following `next_cursor` past the
 * server's default page of 50: the Documents screen lists each non-deleted
 * document from here (T-0160), not just the first page.
 */
export async function listDocumentProcessing(
  engagementId: string,
): Promise<DocumentProcessingList> {
  const path = `/api/v1/engagements/${encodeURIComponent(engagementId)}/documents`;
  const documents: DocumentProcessingItem[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_DOCUMENT_PAGES; page += 1) {
    const res = await apiClient(path, DocumentProcessingListSchema, {
      query: { limit: DOCUMENTS_PAGE_SIZE, cursor },
    });
    documents.push(...res.documents);
    if (!res.next_cursor) break;
    cursor = res.next_cursor;
  }
  return { documents };
}

/** Nothing more will happen to the document without someone acting. */
export function isProcessingSettled(doc: DocumentProcessingItem): boolean {
  if (doc.stage) return doc.stage === 'ready' || doc.stage === 'failed';
  return doc.process_status === 'indexed' || doc.process_status === 'failed';
}

/** How often the list is polled while any document is still moving. */
export const PROCESSING_POLL_MS = 3000;

/**
 * The engagement's documents with their pipeline view. Polls every 3 s while
 * any document is still moving (or the list is empty: an upload may be about
 * to land) and stops once every document has settled. A Retry invalidates
 * the list; the refetch shows the retried stage in progress, so polling
 * resumes.
 *
 * Keyed under `['engagement-documents', engagementId]`, so everything that
 * invalidates the engagement's documents list refreshes this one too.
 */
export function useDocumentProcessingQuery(
  engagementId: string | undefined,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['engagement-documents', engagementId, 'processing'] as const,
    queryFn: () => listDocumentProcessing(engagementId!),
    enabled: Boolean(engagementId) && (opts?.enabled ?? true),
    refetchInterval: (q) => {
      const data = q.state.data as DocumentProcessingList | undefined;
      const docs = data?.documents ?? [];
      if (docs.length === 0) return PROCESSING_POLL_MS;
      return docs.every(isProcessingSettled) ? false : PROCESSING_POLL_MS;
    },
  });
}

export type RetryAction = 'reprocess' | 'reindex';

const RetryAcceptedSchema = z.object({}).passthrough();

/**
 * Retry the failed stage through the existing endpoints:
 *   reindex   → POST /api/v1/documents/:id/reindex { mode: 'failed_only' }
 *   reprocess → POST /api/v1/documents/:id/reprocess
 * Both are allowed to any engagement member who can write documents (the
 * API's existing checks); the API records the queued stage so the list
 * shows it in progress at once.
 */
export function retryDocumentProcessing(documentId: string, action: RetryAction): Promise<void> {
  const path = `/api/v1/documents/${encodeURIComponent(documentId)}/${action}`;
  return apiClient(path, RetryAcceptedSchema, {
    method: 'POST',
    ...(action === 'reindex' ? { body: { mode: 'failed_only' } } : {}),
  }).then(() => undefined);
}

export function useRetryDocumentProcessingMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, action }: { documentId: string; action: RetryAction }) =>
      retryDocumentProcessing(documentId, action),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engagement-documents', engagementId] });
    },
  });
}
